<template lang="pug">
yz-dialog(v-model="showDialog" max-width="1100" persistent scrollable)
  yz-card
    yz-dialog-normal-header(title="Override default pricing" @close="onClose")

    yz-dialog-normal-content.pa-6
      v-form(ref="form")
        //- Section 1: Branch selection
        template(v-if="!hideBranchSelection")
          .yz-heading-8.text-neutral-700 Select your preferred branches
          .yz-regular-body-sm.mt-1 This pricing will override the default pricing and adjusted according to your selected branches and pricing setup.
          yz-card.mt-4(outlined :style="branchError ? 'border-color: var(--yz-danger-500)' : ''")
            .pa-4
              yz-checkbox-group(v-model="selectedBranches" direction="horizontal" :spacing="0")
                v-row(no-gutters)
                  v-col.pa-2(v-for="branch in branches" :key="branch.companyId" cols="4")
                    yz-tooltip(:disabled="isEditMode || branch.enabled" text="Turn on this branch to add override pricing" direction="top")
                      yz-checkbox(:value="branch.companyId" :label="branch.name" :disabled="isEditMode || !branch.enabled")
          .yz-regular-body-xs.text-danger-500.mt-1(v-if="branchError") Please select at least one branch

        //- Section 2: Override pricing table
        .yz-heading-8.text-neutral-700(:class="hideBranchSelection ? '' : 'mt-6'") Override default pricing
        .yz-regular-body-sm.mt-1 This pricing will override the default pricing and adjusted according to your selected branches and pricing setup.
        yz-simple-table.mt-3
          template(v-slot:default)
            thead
              tr
                th.yz-medium-body-xs.text-neutral-700 Price tier
                th.yz-medium-body-xs.text-neutral-700 Payment method
                th.yz-medium-body-xs.text-neutral-700(style="width: 250px;") Default price
                th.yz-medium-body-xs.text-neutral-700(style="width: 250px;") Override price
            tbody
              template(v-for="(tier, tIdx) in overrideTiers")
                tr(:key="'tier-' + tIdx")
                  td.py-3
                    .yz-heading-9.text-neutral-700 {{ tier.name }}
                    .yz-regular-body-sm {{ tier.description }}
                  td.py-3
                    .d-flex.align-center.gap-2
                      div
                        .yz-regular-body-xs.text-neutral-700 Panel: {{ tier.panelText }}
                        .yz-regular-body-xs.text-neutral-700.mt-1 Self-Pay: {{ tier.selfPayText }}
                      yz-icon.text-neutral-500(size="16") mdi-information-outline
                  td.py-3
                    template(v-if="hasUom")
                      .yz-regular-body-sm.text-neutral-500.font-italic Multiple UOMs
                    template(v-else)
                      yz-text-field(:value="tier.defaultPrice" placeholder="0" :prefix="currencySymbol" type="number" size="lg" disabled background-color="neutral200")
                  td.py-3
                    template(v-if="hasUom")
                      .d-flex.align-center.gap-2
                        .yz-regular-body-sm.text-neutral-500.font-italic.flex-grow-1 Multiple UOMs
                        yz-menu(v-model="copyMenuOpen[tIdx]" offset-y left :close-on-content-click="false" min-width="280")
                          template(#activator="{ on, attrs }")
                            yz-icon.cursor-pointer(size="16" v-on="on" v-bind="attrs") mdi-content-copy
                          yz-card
                            .px-4.py-3.border-bottom
                              .yz-heading-9.text-neutral-700 Copy pricing to
                            .overflow-y-auto(style="max-height: 300px;")
                              yz-checkbox-group(v-model="copyTargetTiers[tIdx]" direction="vertical" :spacing="0")
                                template(v-for="(otherTier, otIdx) in overrideTiers")
                                  .px-4.py-3.border-bottom(v-if="otIdx !== tIdx" :key="otherTier.id")
                                    yz-checkbox(:value="otherTier.id" :label="otherTier.name")
                            .d-flex.align-center.justify-space-between.px-4.py-3
                              yz-btn(label="Clear" text size="md" @click="onClearCopyTargets(tIdx)")
                              .yz-heading-10 Selected ({{ copyTargetTiers[tIdx].length }})
                              yz-btn(label="Apply" size="md" @click="onApplyCopyPricing(tIdx)")
                        yz-icon.cursor-pointer(size="16" @click="tier.expanded = !tier.expanded") {{ tier.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    template(v-else)
                      yz-text-field(v-model="tier.overridePrice" placeholder="0" :prefix="currencySymbol" type="number" size="lg" :rules="rules.cost")

                //- UOM sub-rows (both default + override on same row)
                template(v-if="hasUom && tier.expanded")
                  tr(v-for="(uomPrice, uIdx) in tier.defaultPricePerUom" :key="'uom-' + tIdx + '-' + uIdx")
                    td
                    td
                    td.py-2
                      .d-flex.align-center.gap-2
                        .yz-medium-body-sm.text-neutral-700(style="min-width: 80px;") {{ uomPrice.uom }}
                        yz-text-field(:value="uomPrice.price" placeholder="0" :prefix="currencySymbol" type="number" size="lg" disabled background-color="neutral200")
                    td.py-2
                      .d-flex.align-center.gap-2
                        .yz-medium-body-sm.text-neutral-700(style="min-width: 80px;") {{ uomPrice.uom }}
                        yz-text-field(v-model="tier.overridePricePerUom[uIdx].price" placeholder="0" :prefix="currencySymbol" type="number" size="lg" :rules="rules.cost")

    yz-dialog-normal-actions(@cancel="onClose" @save="onSave")
</template>

<script>
export default {
  props: {
    open: { type: Boolean, default: false },
    branches: { type: Array, default: () => [] },
    priceTiers: { type: Array, default: () => [] },
    uomConversions: { type: Array, default: () => [] },
    uomConversionEnabled: { type: Boolean, default: false },
    defaultUom: { type: String, default: null },
    currency: { type: Object, default: () => ({}) },
    existingOverride: { type: Object, default: null },
    hideBranchSelection: { type: Boolean, default: false }
  },
  data() {
    return {
      selectedBranches: [],
      overrideTiers: [],
      copyTargetTiers: [],
      copyMenuOpen: [],
      branchError: false,
      rules: {
        cost: [v => (!v || Number(v) >= 0) || 'Cost must be positive']
      }
    }
  },
  computed: {
    showDialog: {
      get() { return this.open },
      set(val) { this.$emit('update:open', val) }
    },
    hasUom() {
      return this.uomConversionEnabled && this.uomConversions.length > 0
    },
    isEditMode() {
      return !!this.existingOverride
    },
    currencySymbol() {
      return this.currency?.symbol || ''
    },
    uomList() {
      if (this.uomConversions.length === 0) return []
      const uoms = []
      this.uomConversions.forEach(c => {
        if (!uoms.includes(c.uom)) uoms.push(c.uom)
        if (!uoms.includes(c.convert_to)) uoms.push(c.convert_to)
      })
      return uoms
    }
  },
  watch: {
    selectedBranches(val) {
      if (val.length > 0) this.branchError = false
    }
  },
  mounted() {
    this.initOverrideTiers()
  },
  methods: {
    initOverrideTiers() {
      if (this.existingOverride) {
        this.selectedBranches = [...this.existingOverride.branches]
        this.overrideTiers = this.existingOverride.priceTiers.map(pt => {
          const hqTier = this.priceTiers.find(t => t.id === pt.price_tier)
          return {
            id: pt.price_tier,
            name: pt.name || hqTier?.name || '',
            description: pt.description || hqTier?.description || '',
            panelText: hqTier?.panelText || 'None included',
            selfPayText: hqTier?.selfPayText || 'None included',
            defaultPrice: hqTier?.price || null,
            defaultPricePerUom: (hqTier?.price_per_uom || []).map(p => ({ ...p })),
            overridePrice: pt.price,
            overridePricePerUom: this.uomList.map(uom => {
              const saved = pt.price_per_uom?.find(p => p.uom === uom)
              return { uom, price: saved ? saved.price : null }
            }),
            expanded: false
          }
        })
      } else {
        this.selectedBranches = this.hideBranchSelection ? this.branches.map(b => b.companyId) : []
        this.overrideTiers = this.priceTiers.map(tier => ({
          id: tier.id,
          name: tier.name,
          description: tier.description,
          panelText: tier.panelText,
          selfPayText: tier.selfPayText,
          defaultPrice: tier.price,
          defaultPricePerUom: (tier.price_per_uom || []).map(p => ({ ...p })),
          overridePrice: null,
          overridePricePerUom: this.uomList.map(uom => ({ uom, price: null })),
          expanded: false
        }))
      }
      this.copyTargetTiers = this.overrideTiers.map(() => [])
      this.copyMenuOpen = this.overrideTiers.map(() => false)
    },
    onClearCopyTargets(tIdx) {
      this.$set(this.copyTargetTiers, tIdx, [])
    },
    onApplyCopyPricing(tIdx) {
      const sourceTier = this.overrideTiers[tIdx]
      this.copyTargetTiers[tIdx].forEach(targetId => {
        const targetTier = this.overrideTiers.find(t => t.id === targetId)
        if (targetTier) {
          targetTier.overridePricePerUom.forEach((op, i) => {
            op.price = sourceTier.overridePricePerUom[i]?.price ?? null
          })
        }
      })
      this.$set(this.copyTargetTiers, tIdx, [])
      this.$set(this.copyMenuOpen, tIdx, false)
    },
    onSave() {
      this.branchError = !this.hideBranchSelection && this.selectedBranches.length === 0
      if (this.branchError) return
      if (!this.$refs.form.validate()) return
      const overrideData = {
        branches: [...this.selectedBranches],
        priceTiers: this.overrideTiers.map(t => ({
          price_tier: t.id,
          price: this.hasUom ? null : t.overridePrice,
          price_per_uom: t.overridePricePerUom.filter(p => p.price !== null && p.price !== '').map(p => ({ uom: p.uom, price: p.price }))
        }))
      }
      this.$emit('save', overrideData)
    },
    onClose() {
      this.$emit('closed')
    }
  }
}
</script>

<style lang="scss" scoped>
.border-bottom {
  border-bottom: 1px solid var(--yz-neutral-300);
}
</style>
